import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../config/api';

function StatCard({ label, value, subtext }) {
  return (
    <div className="dashboard-stat-card">
      <p className="dashboard-stat-label">{label}</p>
      <h2>{value}</h2>
      <span>{subtext}</span>
    </div>
  );
}

function formatRoleLabel(role) {
  if (!role) {
    return 'Verified User';
  }

  return role.charAt(0).toUpperCase() + role.slice(1);
}

export default function DashboardHome({ username, role, projects, documents, tasks, messages }) {
  const [smpData, setSmpData] = useState(null);
  const [smpLoading, setSmpLoading] = useState(false);
  const [smpError, setSmpError] = useState('');
  const [recData, setRecData] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState('');
  const openTasks = tasks.filter((task) => task.status !== 'Done').length;
  const unreadMessages = messages.filter((message) => !message.read).length;
  const activeProjects = projects.filter((project) => project.status !== 'Completed').length;
  const latestSmp = smpData?.latest || null;
  useEffect(() => {
    const fetchSmp = async () => {
      try {
        setSmpLoading(true);
        setSmpError('');
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('로그인이 필요합니다.');
        }

        const response = await fetch(`${API_BASE_URL}/api/market/smp`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        });
        const data = await response.json();
        if (!(response.ok && data.status === 'success')) {
          throw new Error(data.message || 'SMP 데이터를 불러오지 못했습니다.');
        }
        setSmpData(data);
      } catch (error) {
        console.error(error);
        setSmpError(error.message || 'SMP 데이터를 불러오지 못했습니다.');
      } finally {
        setSmpLoading(false);
      }
    };

    fetchSmp();
  }, []);

  useEffect(() => {
    const fetchRec = async () => {
      try {
        setRecLoading(true);
        setRecError('');
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('로그인이 필요합니다.');
        }

        const response = await fetch(`${API_BASE_URL}/api/market/rec`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        });
        const data = await response.json();
        if (!(response.ok && data.status === 'success')) {
          throw new Error(data.message || 'REC 데이터를 불러오지 못했습니다.');
        }
        setRecData(data);
      } catch (error) {
        console.error(error);
        setRecError(error.message || 'REC 데이터를 불러오지 못했습니다.');
      } finally {
        setRecLoading(false);
      }
    };

    fetchRec();
  }, []);

  return (
    <>
      <header className="dashboard-header">
        <div>
          <p className="dashboard-mini-label">WORKSPACE</p>
          <h1>Welcome back, {username || 'User'}</h1>
          <p className="dashboard-header-text">
            This is your private business dashboard. Review projects, manage documents,
            track tasks, and organize communication from one workspace.
          </p>
        </div>

        <div className="dashboard-user-card">
          <div className="dashboard-user-avatar">
            {(username || 'U').charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="dashboard-user-name">{username || 'User'}</div>
            <div className="dashboard-user-role">{formatRoleLabel(role)}</div>
          </div>
        </div>
      </header>

      <section className="dashboard-stats-grid">
        <StatCard label="Active Projects" value={activeProjects} subtext="Currently being managed" />
        <StatCard label="Open Tasks" value={openTasks} subtext="Items requiring action" />
        <StatCard label="Unread Messages" value={unreadMessages} subtext="Latest communication updates" />
        <StatCard label="Documents" value={documents.length} subtext="Reports and stored files" />
      </section>

      <section className="dashboard-panel dashboard-panel-wide">
        <div className="dashboard-panel-header">
          <h3>전력시장 가격 동향</h3>
        </div>

        <div className="dashboard-market-grid">
          {smpLoading ? (
            <div className="dashboard-smp-empty">전력거래소 SMP 데이터를 불러오는 중입니다.</div>
          ) : smpError ? (
            <div className="dashboard-smp-empty">{smpError}</div>
          ) : latestSmp ? (
            <section className="dashboard-market-section">
              <div className="dashboard-market-section-header">
                <h3>SMP</h3>
              </div>
              <div className="dashboard-smp-grid">
                <div className="dashboard-smp-summary">
                  <p className="dashboard-smp-label">육지 SMP 가중평균</p>
                  <h2>{Number(latestSmp.weighted_average || 0).toFixed(2)}</h2>
                  <span>
                    {smpData.trade_date || `${latestSmp.date} (${latestSmp.weekday})`} 거래분
                  </span>
                  <div className="dashboard-smp-meta">
                    <div>최대 {Number(latestSmp.max || 0).toFixed(2)} 원/kWh</div>
                    <div>최소 {Number(latestSmp.min || 0).toFixed(2)} 원/kWh</div>
                  </div>
                </div>

                <div className="dashboard-smp-trend-wrap">
                  <div className="dashboard-smp-trend-title">Recent Daily Trend</div>
                  <div className="dashboard-smp-table-wrap">
                    <table className="dashboard-smp-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Avg</th>
                          <th>High</th>
                          <th>Low</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(smpData.recent_daily || []).map((item) => (
                          <tr key={`${item.date}-${item.weekday}`}>
                            <td>{item.date} ({item.weekday})</td>
                            <td>{Number(item.weighted_average || 0).toFixed(2)}</td>
                            <td>{Number(item.max || 0).toFixed(2)}</td>
                            <td>{Number(item.min || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <a
                    className="dashboard-smp-source"
                    href={smpData.source_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    전력거래소 SMP 원문 보기
                  </a>
                </div>
              </div>
            </section>
          ) : (
            <div className="dashboard-smp-empty">표시할 SMP 데이터가 없습니다.</div>
          )}

          {recLoading ? (
            <div className="dashboard-smp-empty">전력거래소 REC 데이터를 불러오는 중입니다.</div>
          ) : recError ? (
            <div className="dashboard-smp-empty">{recError}</div>
          ) : recData ? (
            <section className="dashboard-market-section">
              <div className="dashboard-market-section-header">
                <h3>REC</h3>
              </div>
              <div className="dashboard-rec-summary">
                <div className="dashboard-rec-main">
                  <p className="dashboard-smp-label">REC 평균가</p>
                  <h2>{Number(recData.average_price || 0).toLocaleString()}</h2>
                  <span>{recData.trade_date} 기준</span>
                </div>
                <div className="dashboard-rec-grid">
                  <div className="dashboard-rec-item">
                    <span>거래량</span>
                    <strong>{Number(recData.volume || 0).toLocaleString()}</strong>
                  </div>
                  <div className="dashboard-rec-item">
                    <span>종가</span>
                    <strong>{Number(recData.close_price || 0).toLocaleString()}</strong>
                  </div>
                  <div className="dashboard-rec-item">
                    <span>최고가</span>
                    <strong>{Number(recData.high_price || 0).toLocaleString()}</strong>
                  </div>
                  <div className="dashboard-rec-item">
                    <span>최저가</span>
                    <strong>{Number(recData.low_price || 0).toLocaleString()}</strong>
                  </div>
                </div>
                <a
                  className="dashboard-smp-source"
                  href={recData.source_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  전력거래소 REC 원문 보기
                </a>
              </div>
            </section>
          ) : (
            <div className="dashboard-smp-empty">표시할 REC 데이터가 없습니다.</div>
          )}
        </div>
      </section>

    </>
  );
}
